import React, {FC, ReactNode} from 'react';
import {CheckboxProps, TypographyProps, withStyles} from '@material-ui/core';
import Box, {BoxProps} from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import Typography from '@material-ui/core/Typography';
import {getDirectionStyles} from '../../utils/ThemeUtils';
import TooltipPopover from './TooltipPopover';
import TooltipIconButton from './TooltipIconButton';

import {Option} from '../../types';
import {TextEllipsis} from './TextEllipsis';

const StyledOptionWrapper = withStyles(theme => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        width: '100%',

        margin: getDirectionStyles(theme.direction)(
            theme.spacing(0, 0, 0, 4),
            theme.spacing(0, 4, 0, 0)
        )
    }
}))(Box);

const StyledCheckbox = withStyles({
    root: {
        width: '20px',
        height: '20px',
        padding: 0
    }
})(Checkbox);

interface StyledTextContainer extends BoxProps {
    short?: boolean;
}

const StyledTextContainer = withStyles<string, object, StyledTextContainer>(theme => ({
    root: {
        width: '100%',
        marginBottom: '4px',

        '&:last-child': {
            marginBottom: 0
        },

        [theme.breakpoints.up('sm')]: {
            width: p => (p.short ? '61%' : 'auto'),
            marginBottom: 0
        }
    }
}))(({short, ...props}: StyledTextContainer) => <Box {...props} />);

export interface MultipleOptionProps extends Pick<Option, 'label' | 'secondaryText'> {
    selected: boolean;
    children?: ReactNode;
    CheckboxProps?: CheckboxProps;
    TypographyProps?: TypographyProps;
    value?: string | number;
}

const MultipleOption: FC<MultipleOptionProps> = ({
    label,
    secondaryText,
    value,
    selected,
    children,
    CheckboxProps: checkboxProps,
    TypographyProps: typographyProps
}: MultipleOptionProps) => {
    const [open, setOpen] = React.useState<boolean>(false);

    const handlePopoverOpen = () => {
        setOpen(true);
    };

    const handlePopoverClose = () => {
        setOpen(false);
    };

    return (
        <>
            <StyledCheckbox checked={selected} color="primary" {...checkboxProps} />
            <StyledOptionWrapper>
                <StyledTextContainer clone short={Boolean(secondaryText || children)} mr={1}>
                    <TextEllipsis variant="body1" color="textPrimary" {...typographyProps}>
                        <TooltipPopover open={open} title={label} secondaryText={value}>
                            <span>{label}</span>
                        </TooltipPopover>
                    </TextEllipsis>
                </StyledTextContainer>
                {secondaryText && (
                    <Typography component="span" color="textSecondary" variant="caption">
                        {secondaryText}
                    </Typography>
                )}
                {children}
            </StyledOptionWrapper>
            {value && (
                <TooltipIconButton
                    isOpen={open}
                    onPopoverOpen={handlePopoverOpen}
                    onPopoverClose={handlePopoverClose}
                />
            )}
        </>
    );
};

export default MultipleOption;
